import { Matrix, EigenvalueDecomposition } from 'ml-matrix'

/**
 * Führt Principal Coordinates Analysis (PCoA) auf einer Distanzmatrix durch.
 * @param distMatrix quadratische Distanzmatrix (n x n)
 * @param dimensions Anzahl der gewünschten Achsen (z. B. 2)
 * @returns Array mit PCoA-Koordinaten (n × dimensions)
 */
export function pcoa(distMatrix: number[][], dimensions: number = 2): number[][] {
  const n = distMatrix.length

  // 1️⃣ Quadrat der Distanzen berechnen
  const distSquared = distMatrix.map((row) => row.map((d) => d ** 2))
  const D2 = new Matrix(distSquared)

  // 2️⃣ Zentrierungsmatrix J = I - 1/n * 11ᵀ
  const I = Matrix.eye(n)
  const ones = Matrix.ones(n, n)
  const J = I.sub(ones.mul(1 / n))

  // 3️⃣ B = -0.5 * J * D² * J
  const B = J.mmul(D2).mmul(J).mul(-0.5)

  // 4️⃣ Eigenwertzerlegung von B
  const eig = new EigenvalueDecomposition(B)
  const eigenValues = eig.realEigenvalues
  const eigenVectors = eig.eigenvectorMatrix

  // 5️⃣ Eigenwerte/Vektoren sortieren (größter zuerst)
  const indices = eigenValues
    .map((v, i) => [v, i])
    .sort((a, b) => b[0] - a[0])
    .map((x) => x[1])

  // 6️⃣ Koordinatenmatrix aufbauen
  const coords: number[][] = Array.from({ length: n }, () => Array(dimensions).fill(0))

  for (let d = 0; d < dimensions; d++) {
    const eigVal = eigenValues[indices[d]]
    if (eigVal <= 0) continue // negative Eigenwerte ignorieren
    const scale = Math.sqrt(eigVal)
    for (let i = 0; i < n; i++) {
      coords[i][d] = eigenVectors.get(i, indices[d]) * scale
    }
  }

  return coords
}
